import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import useLocalStorageState from "use-local-storage-state"
import { useNavigate } from "react-router-dom"
import { RxCrossCircled } from "react-icons/rx"
import Select from "react-select"

import { Form, Modal, Button } from "react-bootstrap" // Import Modal from react-bootstrap
import { GetRequest, PostRequest, PutRequest } from "../Utils/hooks/ApiHandler"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"


const whatsappSchema = z.object({
    templateId: z.string().min(1, "Template ID is required"),
    templateContent: z.string().min(1, "Template content is required"),
})

export const WhatsappModal = ({ whatsappModalOpen, setWhatsAppModalOpen, id, module_name }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [templateModuleOption, setTemplateModuleOption] = useState([])
    const [whatsappTemplateData, setWhatsappTemplateData] = useState(null);
    const [currentTemplateId, setCurrentTemplateId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [customer, setCustomer] = useLocalStorageState("customer")
    // Declare dynamic fields and related state at the top
    const [dynamicFields, setDynamicFields] = useState([]);
    const [templateVariables, setTemplateVariables] = useState([]);
    const [templateVariableTypes, setTemplateVariableTypes] = useState({});
    const [variableCount, setVariableCount] = useState(0);

    const customerId = customer.customer_details?.customer_id
    const token = customer?.token?.authorization
    const rf = customer?.token?.authentication
    const headers = {
        authentication: rf,
        Authorization: token,
    }
    const navigate = useNavigate()
    const extras = { customer, setCustomer, rf: headers.authentication, navigate }


    // Create dynamic schema for WhatsApp form based on dynamic fields
    const createWhatsAppSchema = () => {
        const dynamicFieldValidations = {};

        dynamicFields.forEach((field) => {
            if (field.type === "image") {
                dynamicFieldValidations[field.name] = z
                    .instanceof(FileList)
                    .refine((files) => files?.length > 0, "Image is required");
            } else if (field.type === "url") {
                dynamicFieldValidations[field.name] = z
                    .string()
                    .min(1, `${field.label} is required`)
                    .url("Please enter a valid URL");
            } else {
                dynamicFieldValidations[field.name] = z
                    .string()
                    .min(1, `${field.label} is required`);
            }
        });

        return whatsappSchema.extend(dynamicFieldValidations);
    };

    const {
        handleSubmit,
        watch,
        register,
        reset,
        setValue,
        control,
        formState: { errors, isValid },
    } = useForm({
        resolver: zodResolver(createWhatsAppSchema()),
        mode: "onChange",
    })

    const resetFormStates = () => {
        reset()
    }

    useEffect(() => {
        ; (async () => {
            try {
                const res = await GetRequest(
                    `common/fetchAllMsg91Template_moduleName/${module_name}/${customer?.customer_details?.customer_id}`,
                    { headers },
                    extras,
                )
                if (res.status === "fail") {
                    return
                }
                const options = res.data.map((item) => ({
                    value: item.id,
                    label: item.template_name,
                }))
                setTemplateModuleOption(options)
            } catch (error) {
                toast.error("Error fetching Template Module Name")
                return
            }
        })()
    }, [])


    const templateId = watch("templateId");
    useEffect(() => {
        if (templateId !== null) {
            setValue('templateContent', '')
            setDynamicFields([])
        }
    }, [templateId])

    const onWhatsAPPFetch = async () => {
        if (!templateId) {
            toast.error("Please select a template first");
            return;
        }
        const template = templateModuleOption.find((temp) => templateId === temp.value)



        setIsLoading(true);
        try {
            const res = await GetRequest(
                `/integration/fetchWhatsappTemplateContent/${template && template.label}/${customerId}`,
                { headers },
                extras
            );

            if (res.status === "success") {
                const templateData = JSON.parse(res.data);
                setWhatsappTemplateData(templateData.data);

                const bodyContent = templateData.data[0].languages[0].code.find(
                    (item) => item.type === "BODY"
                );
                if (bodyContent) {
                    setValue("templateContent", bodyContent.text || "");
                }

                const variables = templateData.data[0].languages[0].variables || [];
                const variableTypes =
                    templateData.data[0].languages[0].variable_type || {};

                setTemplateVariables(variables);
                setTemplateVariableTypes(variableTypes);

                let count = 0;
                const fields = variables.map((variable) => {
                    const varType = variableTypes[variable]?.type;
                    let fieldType = "text";
                    let label = variable;

                    if (variable.startsWith("header_")) {
                        count++;
                        fieldType = varType;
                        label = "Header";
                    } else if (variable.startsWith("button_")) {
                        count++;
                        fieldType = varType;
                        label = "Button URL";
                    } else if (variable.startsWith("body_")) {
                        count++;
                        label = `Body Text ${variable.split("_")[1]}`;
                    }

                    return {
                        name: variable,
                        type: fieldType,
                        label: label,
                        required: true,
                    };
                });

                setDynamicFields(fields);
                setVariableCount(count);
                toast.success("Template fetched successfully");
            } else {
                toast.error(res.message || "Failed to fetch template");
            }
        } catch (error) {
            console.error("Error fetching WhatsApp template:", error);
            toast.error("Failed to fetch template");
        } finally {
            setIsLoading(false);
        }
    };
    // Render dynamic fields based on template variables
    const renderDynamicFields = () => {
        return dynamicFields.map((field, index) => {
            if (field.type === "image") {
                return (
                    <div key={index} className="col-md-12">
                        <label className="form-label">
                            {field.label} <span className="text-danger">*</span>
                        </label>
                        <input
                            type="file"
                            {...register(field.name, {
                                required: `${field.label} is required`,
                            })}
                            className={`form-control mb-2 `}
                            accept="image/*"
                        />
                        {errors[field.name] && (
                            <div className="text-danger small mt-1">
                                {errors[field.name].message}
                            </div>
                        )}
                    </div>
                );
            } else if (field.type === "url") {
                return (
                    <div key={index} className="col-md-12">
                        <label className="form-label">
                            {field.label} <span className="text-danger">*</span>
                        </label>
                        <input
                            type="url"
                            {...register(field.name, {
                                required: `${field.label} is required`,
                                validate: (value) => {
                                    try {
                                        new URL(value);
                                        return true;
                                    } catch {
                                        return "Please enter a valid URL";
                                    }
                                },
                            })}
                            className={`form-control mb-2 `}
                            placeholder={`Enter ${field.label}`}
                        />
                        {errors[field.name] && (
                            <div className="text-danger small mt-1">
                                {errors[field.name].message}
                            </div>
                        )}
                    </div>
                );
            } else {
                return (
                    <div key={index} className="col-md-12">
                        <label className="form-label">
                            {field.label} <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            {...register(field.name, {
                                required: `${field.label} is required`,
                            })}
                            className={`form-control mb-2 `}
                            placeholder={`Enter ${field.label}`}
                        />
                        {errors[field.name] && (
                            <div className="text-danger small mt-1">
                                {errors[field.name].message}
                            </div>
                        )}
                    </div>
                );
            }
        });
    };

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            const variables = [];
            const variableValues = [];
            const variableTypes = [];
            let bodyCount = 0;
            let header_text = null
            let header_image = null
            dynamicFields.forEach((field) => {
                variables.push(field.name);
                variableTypes.push(field.type);
                if (field.name.startsWith("body_")) {
                    bodyCount++;
                }
                // Handle file inputs differently
                if (field.type === "image") {
                    // For files, we might need to upload them separately or convert to base64
                    // Here we're just getting the file name for demonstration

                    header_image = data.header_1[0] || null
                    // header_image = data[field.name]?.[0]?.name || null
                } else if (field.type === "text" && field.name === "header_1") {
                    header_text = data.header_1 || null
                } else {
                    variableValues.push(data[field.name] || "");
                }
            });

            const payload = {
                customer_id: customerId,
                template_name: whatsappTemplateData?.[0]?.name || "",
                template_namespace: whatsappTemplateData?.[0]?.namespace || "",
                template_language:
                    whatsappTemplateData?.[0]?.languages?.[0]?.language || "en",
                template_template_id:
                    whatsappTemplateData?.[0]?.languages?.[0]?.id || "",
                template_variable_count: variables.length.toString(),
                template_variable_type: variables,
                template_variable_typeOFtype: variableTypes,
                template_body_count: bodyCount.toString(),
                template_content: data.templateContent,
                variables_value: variableValues,
                user_login_id: customer.customer_details?.id,
                module_name: module_name,
                to_id: id,
                variables_value_url: data?.button_1 || null,
                header_image: header_image,
                header_text: header_text
            };
            const res = await PostRequest('/common/sendWhatsappMsg', payload, true, { headers }, extras)
            if (res.status === "success") {
                toast.success(
                    res.message
                );
                setWhatsAppModalOpen(false);
                resetFormStates();
            } else {
                toast.error(
                    res.message
                );
            }
        } catch (error) {
            toast.error(`Failed to ${isEditMode ? "update" : "submit"} template`);
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Modal
            show={true} // This prop should be controlled by the parent component's state (whatsappModalOpen)
            onHide={() => {
                setWhatsAppModalOpen(false)
                resetFormStates()
            }}
            centered
            size="lg"
        >

            <Modal.Header className="border-bottom pb-2">
                <div className="d-flex align-items-start justify-content-between w-100">
                    <div>
                        <Modal.Title className="mb-0" style={{ display: 'flex', alignItems: 'center', color: 'black', fontSize: '19px', fontWeight: 500, }}>Add WhatsApp Template</Modal.Title>
                        <small style={{ fontWeight: '300', fontSize: '14px', color: "#848484" }}>Add a new WhatsApp template for notifications</small>
                    </div>
                    <button
                        type='button' className='btn btn-default btnss'
                        onClick={() => {
                            setWhatsAppModalOpen(false)
                            resetFormStates()
                        }}
                        style={styles.listButtonsStyles}
                    >
                        <FontAwesomeIcon icon={faXmark} className='ms-0' />
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3">
                        {/* template id dropdown */}
                        <div className="col-md-12">
                            <label className="form-label">
                                Template <span className="text-danger">*</span>
                            </label>
                            <div className="d-flex gap-2">
                                <Controller
                                    name="templateId"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className="input-box-select w-100"
                                            options={templateModuleOption}
                                            placeholder="Select a template"
                                            isClearable
                                            isSearchable
                                            onChange={(selected) => field.onChange(selected?.value)}
                                            value={templateModuleOption.find((option) => option.value === field.value) || null}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    )}
                                />
                                <Button
                                    variant="default"
                                    onClick={onWhatsAPPFetch}
                                    disabled={isLoading || !watch("templateId")}
                                >
                                    {isLoading ? "Fetching..." : "Fetch"}
                                </Button>
                            </div>
                            {errors.templateId && <div className="text-danger small mt-1">{errors.templateId.message}</div>}
                        </div>
                        {/* template content */}
                        <div className="col-md-12">
                            <label className="form-label">
                                Template Content <span className="text-danger">*</span>
                            </label>
                            <textarea
                                {...register("templateContent")}
                                className={`form-control ${errors.templateContent ? "is-invalid" : ""}`}
                                rows={10}
                                placeholder="Template Content"
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    minHeight: "100px",
                                    resize: "none",
                                    overflow: "none",
                                }}
                                readOnly={true}
                            />
                        </div>
                        {/* Dynamic Fields Section */}
                        {dynamicFields.length > 0 && (
                            <div className="col-md-12">
                                <h5 className="mb-3">Template Variables</h5>
                                <div className="row g-3">{renderDynamicFields()}</div>
                            </div>
                        )}
                        {errors.templateContent && <div className="text-danger small mt-1">{errors.templateContent.message}</div>}
                    </div>
                    {/* save and cancel button */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button
                            className="btn-default-outline"
                            onClick={() => {
                                setWhatsAppModalOpen(false)
                                resetFormStates()
                            }}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn-default"
                            style={{ backgroundColor: "rgb(122,58,94)", color: "white" }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {"Saving..."}
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>

                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    )
}
const styles = {
    listButtonsStyles: {
        fontSize: '13px',
        // borderBottom: '1px solid #e3e3e3',
        height: '30px',
        width: 'auto',
        gap: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '2px',
        color: '#fff'
    },
}